import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { Card, Button } from '../components';
import {
  User, Clock, Upload, Briefcase, GraduationCap, Plus, Trash2, Video, Receipt
} from 'lucide-react';
import { ProfielFacturenTab } from '../components/professionalFacturen/ProfielFacturenTab';
import { useCreateProfile, useProfileStatus, usePortfolioStatus, usePublishPortfolio } from '../hooks/useProfessionalProfile';
import api from '../services/api';
import BelagomProfilePhoto from '../components/BelagomProfilePhoto';
import ReisradiusMap from '../components/ReisradiusMap';
import TagSelector from '../components/TagSelector';
import TijdslotAgenda, { type Tijdslot } from '../components/TijdslotAgenda';
import { useProfileTaxonomy } from '../hooks/useProfileTaxonomy';
import { useTranslation } from 'react-i18next';

type ProfileData = z.infer<ReturnType<typeof createProfileSchema>>;

/** FO stap 3: bio's zijn begrensd op 300 woorden per veld */
const MAX_BIO_WOORDEN = 300;

/** Woorden tellen zoals de backend het doet: witruimte-gescheiden tokens */
const countWords = (text?: string | null) =>
  text ? text.trim().split(/\s+/).filter(Boolean).length : 0;

function createProfileSchema(t: (key: string) => string) {
  return z.object({
    // Personal Information
    profile_photo: z.any().optional(),
    introduction: z.string()
      .min(10, t('validation.introTooShort'))
      .max(750, t('validation.introTooLong'))
      .optional(),
    // FO stap 3 — harde splitsing van de bio in twee doelgroepteksten
    bio_medewerker: z.string()
      .min(1, t('validation.bioMedewerkerRequired'))
      .refine((val) => countWords(val) <= MAX_BIO_WOORDEN, t('validation.bioTooLong')),
    bio_werkgever: z.string()
      .min(1, t('validation.bioWerkgeverRequired'))
      .refine((val) => countWords(val) <= MAX_BIO_WOORDEN, t('validation.bioTooLong')),
    // FO stap 7 — introductievideo als YouTube/Vimeo-link
    intro_video_url: z.string()
      .optional()
      .refine(
        (val) => !val || /^https:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|vimeo\.com\/)/i.test(val),
        t('validation.videoUrlInvalid')
      ),
    first_name: z.string().min(2, t('validation.firstNameRequired')),
    last_name: z.string().min(2, t('validation.lastNameRequired')),
    birth_date: z.string()
      .min(1, t('validation.birthDateRequired'))
      .transform((val) => {
        if (!val) return undefined;
        const date = new Date(val);
        if (isNaN(date.getTime())) return undefined;
        return date.toISOString().split('T')[0];
      })
      .refine((val) => {
        if (!val) return false;
        const date = new Date(val);
        const now = new Date();
        const age = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24 * 365.25));
        return age >= 18 && age <= 100;
      }, t('validation.minAge18')),
    phone: z.string().min(10, t('validation.phoneRequired')),
    email: z.string().email(t('validation.invalidEmail')),

    // Address
    street_address: z.string().min(5, t('validation.streetRequired')),
    postal_code: z.string().regex(/^[1-9][0-9]{3} ?[A-Z]{2}$/i, t('validation.invalidPostalCode')),
    city: z.string().min(2, t('validation.cityRequired')),
    country: z.string().min(2, t('validation.countryRequired')),

    // Professional Details
    years_self_employed: z.union([
      z.number(),
      z.string()
    ]).transform((val) => {
      if (typeof val === 'string') {
        const parsed = parseInt(val, 10);
        if (isNaN(parsed)) {
          throw new Error(t('validation.invalidYears'));
        }
        return parsed;
      }
      return val;
    }).refine((val) => val >= 0, {
      message: t('validation.yearsNotNegative')
    }),
    // hourly_rate removed - rates are determined automatically based on KVK date and professional type
    kvk_nummer: z.string().optional().refine((val) => {
      if (!val) return true; // Optional field
      return /^\d{8}$/.test(val);
    }, {
      message: t('validation.kvk8Digits')
    }),
    kvk_registration_years: z.union([
      z.number(),
      z.string()
    ]).transform((val) => {
      if (typeof val === 'string') {
        const parsed = parseInt(val, 10);
        if (isNaN(parsed)) {
          return 0; // Default value for optional field
        }
        return parsed;
      }
      return val;
    }).refine((val) => val >= 0, {
      message: t('validation.kvkYearsNotNegative')
    }).optional(),
    kvk_registration_date: z.string().optional().refine((val) => {
      if (!val) return true; // Optional field
      const date = new Date(val);
      if (isNaN(date.getTime())) return false;
      const today = new Date();
      return date <= today; // Must be in the past
    }, {
      message: t('validation.kvkDatePast')
    }),

    // Availability — Wet DBA: geen vast urenaantal, alleen agenda-tijdslots
    availability_status: z.enum(['not_available', 'immediately', 'specific_date']),
    available_from_date: z.string().optional(),
    // Dubbele status-grendel: cliëntenstop voor nieuwe pools + capaciteitslimiet
    accepteer_nieuwe_pools: z.boolean(),
    max_actieve_trajecten: z.number().min(1).max(50),
    // 100% online werkwijze (BLAUW) — reistijdmatrix blijft dan op € 0
    werkt_alleen_online: z.boolean(),

    // Regions & Languages
    languages: z.array(z.string()).min(1, t('validation.selectOneLanguage')),
    // Legacy provincie-selectie (vervangen door reisradius) — optioneel
    active_regions: z.array(z.string()).optional(),
    // Dynamische reisradius vanaf KvK-kantoor; null = heel Nederland
    reisradius_km: z.number().min(10).max(100).nullable(),

    // FO stap 4 — expertise- en doelgroeptags voor de HR-zoekmachine
    specialisatie_tags: z.array(z.string()).min(1, t('validation.selectOneSpecialisation')),
    doelgroep_niveaus: z.array(z.string()).min(1, t('validation.selectOneTargetGroup')),

    // Other — 'werkwijze' en 'eigen auto' zijn vervallen (FO)
    show_employers_on_profile: z.boolean(),
    // FO stap 5 — branche-affiniteit
    affinity: z.array(z.string()).min(1, t('validation.selectOneIndustry')),
    language_knowledge_explanation: z.string().optional(),

    // Work Experience
    work_experience: z.array(z.object({
      company: z.string().min(1, t('validation.companyRequired')),
      position: z.string().min(1, t('validation.positionRequired')),
      start_date: z.string().min(1, t('validation.startDateRequired')),
      end_date: z.string().optional(),
      current: z.boolean(),
      description: z.string().min(10, t('validation.descriptionMin10'))
    })).min(1, t('validation.minOneExperience')),

    // Education
    education: z.array(z.object({
      institution: z.string().min(1, t('validation.institutionRequired')),
      degree: z.string().min(1, t('validation.degreeRequired')),
      field: z.string().min(1, t('validation.fieldRequired')),
      start_year: z.union([z.number(), z.string()]).transform((val) => {
        const parsed = typeof val === 'string' ? parseInt(val, 10) : val;
        return isNaN(parsed) ? new Date().getFullYear() : parsed;
      }).refine((val) => val >= 1950, { message: t('validation.validStartYear') }),
      end_year: z.union([z.number(), z.string(), z.undefined()]).transform((val) => {
        if (val === undefined || val === '') return undefined;
        const parsed = typeof val === 'string' ? parseInt(val, 10) : val;
        return isNaN(parsed) ? undefined : parsed;
      }).optional(),
      current: z.boolean()
    })).min(1, t('validation.minOneEducation'))
  });
}

export const ProfessionalProfilePage: React.FC = () => {
  const { t } = useTranslation('profile');
  const [isLoading, setIsLoading] = useState(false);
  const { t: tFacturen } = useTranslation('professionalFacturen');
  // Deeplink /professional/profile?tab=facturen (Mijn Cockpit → Profiel → Facturen).
  const [activeTab, setActiveTab] = useState(() =>
    new URLSearchParams(window.location.search).get('tab') === 'facturen' ? 'facturen' : 'personal'
  );
  const [, forceUpdate] = useState({});

  // Language selection state - Nederlands is default
  const allLanguages: string[] = t('belagom:constant.languages', { returnObjects: true }) as unknown as string[];
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([allLanguages[0] || 'Nederlands']);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>(allLanguages.filter(l => l !== (allLanguages[0] || 'Nederlands')));

  // Force re-render function
  const triggerRerender = () => forceUpdate({});
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [introVideo, setIntroVideo] = useState<string | null>(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string[] }>({});

  const createProfile = useCreateProfile();
  const { data: profileStatus, isLoading: profileLoading, refetch: refetchProfileStatus } = useProfileStatus();

  // FO stap 4/5 — taxonomie (specialismen, doelgroepen, branches) van de backend
  const { data: taxonomy } = useProfileTaxonomy();
  const minTijdslots = taxonomy?.min_tijdslots ?? 3;
  const maxSpecialisatieTags = taxonomy?.max_specialisatie_tags ?? 8;

  // FO stap 8 — wekelijkse tijdslots; los van het zod-schema omdat ze in een
  // eigen tabel leven en als geheel worden meegestuurd.
  const [availabilitySlots, setAvailabilitySlots] = useState<Tijdslot[]>([]);

  // FO stap 9/10 — compleetheid, preview en publicatie
  const { data: portfolioStatus, refetch: refetchPortfolioStatus } = usePortfolioStatus();
  const publishPortfolio = usePublishPortfolio();

  // Fetch professional screening to get selected types and their rates
  const { data: screeningData } = useQuery({
    queryKey: ['screening-status'],
    queryFn: async () => {
      const response = await api.get('/screening/status');
      return response.data;
    },
    enabled: true, // Always fetch to get professional types
  });

  // Profile schema - inside component so t() is available
  const profileSchema = React.useMemo(() => createProfileSchema(t), [t]);

  const { register, handleSubmit, formState: { errors }, watch, setValue, getValues, reset, trigger } = useForm<ProfileData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      country: 'Nederland',
      availability_status: 'immediately',
      accepteer_nieuwe_pools: true,
      max_actieve_trajecten: 15,
      werkt_alleen_online: false,
      show_employers_on_profile: false,
      languages: [allLanguages[0] || 'Nederlands'],
      active_regions: [],
      reisradius_km: 50,
      affinity: [],
      bio_medewerker: '',
      bio_werkgever: '',
      intro_video_url: '',
      specialisatie_tags: [],
      doelgroep_niveaus: [],
      language_knowledge_explanation: '',
      work_experience: [],
      education: [],
      kvk_nummer: '',
      kvk_registration_date: '',
      kvk_registration_years: 0
    }
  });

  // LinkedIn autofill: open popup to start OAuth and listen for postMessage
  const openLinkedInPopup = async () => {
    try {
      // Ask backend for the LinkedIn authorization URL
      const res = await fetch('/api/auth/linkedin/start', { credentials: 'include' });
      const data = await res.json();
      if (data?.url) {
        const width = 600; const height = 700;
        const left = window.screenX + (window.innerWidth - width) / 2;
        const top = window.screenY + (window.innerHeight - height) / 2;
        window.open(data.url, 'linkedin_oauth', `width=${width},height=${height},left=${left},top=${top}`);
      } else {
        toast.error(t('toast.linkedInAuthError'));
      }
    } catch (error) {
      console.error('LinkedIn start error:', error);
      toast.error(t('toast.linkedInAuthError'));
    }
  };

  React.useEffect(() => {
    const handler = (event: MessageEvent) => {
      // Debug: log incoming message origin and payload
      try {
        // Log for debugging so we can see if the message arrives and its shape
  // eslint-disable-next-line no-console
  console.log('Received postMessage', { origin: event.origin, data: event.data });

        // Basic origin check: log mismatch but allow during testing. If you want
        // to enforce origin, replace this behavior with a strict equality check.
        const currentOrigin = window.location.origin;
        if (event.origin !== currentOrigin && event.origin !== 'https://belagom.demandcluster.com') {
          // eslint-disable-next-line no-console
          console.warn('postMessage origin does not match current origin. Proceeding for now (relaxed check).', { expected: currentOrigin, actual: event.origin });
          // DO NOT return; accept message but keep a warning in the console for debugging
        }
      } catch (e) {
        // If URL parsing fails for any reason, log and continue
        // eslint-disable-next-line no-console
        console.warn('postMessage origin check failed but continuing', e);
      }

      if (event.data && event.data.type === 'linkedin_profile') {
        processLinkedinProfile(event.data.data);
      }

      // If popup notifies to refresh server-side profile data, refetch
      if (event.data && event.data.type === 'linkedin_refresh') {
        try { refetchProfileStatus(); } catch (e) { console.warn('Failed to refetch profile status', e); }
      }
    };

    window.addEventListener('message', handler);

    // Storage event fallback: listen for linkedin_profile_popup written by the popup
    const storageHandler = (ev: StorageEvent) => {
      try {
        if (ev.key === 'linkedin_profile_popup' && ev.newValue) {
          const parsed = JSON.parse(ev.newValue);
          if (parsed && parsed.data) processLinkedinProfile(parsed.data);
        }
        if (ev.key === 'linkedin_profile_popup_refresh' && ev.newValue) {
          try { refetchProfileStatus(); } catch (e) { console.warn('Failed to refetch profile status via storage event', e); }
        }
        if (ev.key === 'linkedin_profile_popup_error' && ev.newValue) {
          const parsed = JSON.parse(ev.newValue);
          // eslint-disable-next-line no-console
          console.warn('LinkedIn popup reported error via storage:', parsed);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Failed to parse storage event for linkedin_profile_popup', e);
      }
    };

    window.addEventListener('storage', storageHandler);

    // On mount, check if popup already wrote profile to localStorage (in case event missed)
    try {
      const raw = localStorage.getItem('linkedin_profile_popup');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.data) processLinkedinProfile(parsed.data);
      }
    } catch { /* ignore */ }

    return () => {
      window.removeEventListener('message', handler);
      window.removeEventListener('storage', storageHandler);
    };
    // Listeners eenmalig registreren; de handlers gebruiken alleen stabiele form-/query-functies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue]);

  // Helper: centralizes logic to apply a LinkedIn profile object to the form
  const processLinkedinProfile = (profile: any) => {
    if (!profile) return;
    const firstName = profile.firstName || profile.given_name || profile.first_name || null;
    const lastName = profile.lastName || profile.family_name || profile.last_name || null;
    const email = profile.email || profile.emailAddress || profile.mail || null;

    // Debug: log incoming profile and current form values
    // eslint-disable-next-line no-console
    console.log('Applying LinkedIn profile to form', { profile, resolved: { firstName, lastName, email }, beforeValues: getValues() });

    if (firstName) setValue('first_name', String(firstName).trim(), { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    if (lastName) setValue('last_name', String(lastName).trim(), { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    if (email) setValue('email', String(email).trim(), { shouldDirty: true, shouldTouch: true, shouldValidate: true });

    try { trigger(['first_name', 'last_name', 'email']); } catch { /* ignore */ }

    const setDomValue = (fieldName: string, value: string | null) => {
      if (!value) return;
      try {
        const byId = document.getElementById(fieldName) as HTMLInputElement | null;
        const byName = document.querySelector(`input[name="${fieldName}"], textarea[name="${fieldName}"], select[name="${fieldName}"]`) as HTMLInputElement | null;
        const el = byId || byName;
        if (el) {
          el.value = String(value);
          el.dispatchEvent(new Event('input', { bubbles: true }));
          el.dispatchEvent(new Event('change', { bubbles: true }));
          // eslint-disable-next-line no-console
          console.log('Set DOM value for', fieldName, '->', value);
        } else {
          // eslint-disable-next-line no-console
          console.log('DOM element not found for', fieldName);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Failed to set DOM value for', fieldName, e);
      }
    };
    console.log("works",firstName,email)
    setDomValue('first_name', firstName);
    setDomValue('last_name', lastName);
    setDomValue('email', email);

    // Debug: show form values after attempting to set
    // eslint-disable-next-line no-console
    console.log('After applying profile, form values:', getValues(), {
      dom_first_name: (document.getElementById('first_name') as HTMLInputElement | null)?.value || null,
      dom_last_name: (document.getElementById('last_name') as HTMLInputElement | null)?.value || null,
      dom_email: (document.querySelector('input[name="email"]') as HTMLInputElement | null)?.value || null
    });

    try {
      const el = document.getElementById('first_name') as HTMLInputElement | null;
      if (el) el.focus();
    } catch { /* ignore */ }

    toast.success(t('toast.linkedInSuccess'));
  };

  // Load existing profile data when available
  React.useEffect(() => {
    if (profileStatus?.data?.profile_complete && profileStatus?.data?.profile) {
      const existingProfile = profileStatus.data.profile;
      console.log('Loading existing profile:', existingProfile);

      // Reset form with existing profile data
      reset({
        first_name: existingProfile.first_name || '',
        last_name: existingProfile.last_name || '',
        introduction: existingProfile.introduction || '',
        birth_date: existingProfile.birth_date ? existingProfile.birth_date.split('T')[0] : '',
        phone: existingProfile.phone || '',
        email: existingProfile.email || '',
        street_address: existingProfile.street_address || '',
        postal_code: existingProfile.postal_code || '',
        city: existingProfile.city || '',
        country: existingProfile.country || 'Nederland',
        years_self_employed: existingProfile.years_self_employed || 0,
        kvk_nummer: existingProfile.kvk_nummer || screeningData?.data?.screening?.kvk_nummer || '',
        kvk_registration_years: existingProfile.kvk_registration_years || 0,
        kvk_registration_date: existingProfile.kvk_registration_date
          ? new Date(existingProfile.kvk_registration_date).toISOString().split('T')[0]
          : (screeningData?.data?.screening?.opgericht_datum
            ? new Date(screeningData.data.screening.opgericht_datum).toISOString().split('T')[0]
            : ''),
        availability_status: existingProfile.availability_status || 'immediately',
        available_from_date: existingProfile.available_from_date || '',
        accepteer_nieuwe_pools: (existingProfile as any).accepteer_nieuwe_pools ?? true,
        max_actieve_trajecten: (existingProfile as any).max_actieve_trajecten ?? 15,
        werkt_alleen_online: (existingProfile as any).werkt_alleen_online ?? false,
        show_employers_on_profile: existingProfile.show_employers_on_profile || false,
        bio_medewerker: existingProfile.bio_medewerker || '',
        bio_werkgever: existingProfile.bio_werkgever || '',
        intro_video_url: existingProfile.intro_video_url || '',
        specialisatie_tags: existingProfile.specialisatie_tags || [],
        doelgroep_niveaus: existingProfile.doelgroep_niveaus || [],
        languages: existingProfile.languages || [],
        active_regions: existingProfile.active_regions || [],
        reisradius_km: (existingProfile as any).reisradius_km ?? null,
        affinity: existingProfile.affinity || existingProfile.industry_experience || [],
        language_knowledge_explanation: existingProfile.language_knowledge_explanation || '',
        work_experience: existingProfile.work_experience || [],
        education: existingProfile.education || []
      });

      // Tijdslots staan buiten het formulier (eigen tabel) — apart inladen
      if (profileStatus.data.availability_slots) {
        setAvailabilitySlots(profileStatus.data.availability_slots);
      }

      // Set intro video if exists
      if ((existingProfile as any).intro_video_path) {
        const videoPath = (existingProfile as any).intro_video_path;
        let fullVideoPath: string;
        if (videoPath.startsWith('http://') || videoPath.startsWith('https://')) {
          fullVideoPath = videoPath;
        } else if (videoPath.startsWith('/uploads/')) {
          fullVideoPath = videoPath;
        } else if (videoPath.startsWith('uploads/')) {
          fullVideoPath = `/${videoPath}`;
        } else {
          fullVideoPath = `/uploads/${videoPath}`;
        }
        setIntroVideo(fullVideoPath);
      }

      // Set profile photo if exists
      if ((existingProfile as any).profile_photo_path) {
        const photoPath = (existingProfile as any).profile_photo_path;
        // Build the full URL path
        let fullPath: string;
        if (photoPath.startsWith('http://') || photoPath.startsWith('https://')) {
          // Already a full URL
          fullPath = photoPath;
        } else if (photoPath.startsWith('/uploads/')) {
          // Already has /uploads/ prefix
          fullPath = photoPath;
        } else if (photoPath.startsWith('uploads/')) {
          // Has uploads/ but missing leading slash
          fullPath = `/${photoPath}`;
        } else {
          // Relative path, add /uploads/ prefix
          fullPath = `/uploads/${photoPath}`;
        }
        console.log('Setting profile photo:', fullPath);
        setProfilePhoto(fullPath);
      }
    }
  }, [profileStatus, reset, screeningData]);

  // Auto-fill KVK date from screening if available
  React.useEffect(() => {
    if (screeningData?.data?.screening?.opgericht_datum && !watch('kvk_registration_date')) {
      const kvkDate = new Date(screeningData.data.screening.opgericht_datum).toISOString().split('T')[0];
      setValue('kvk_registration_date', kvkDate);
    }
    if (screeningData?.data?.screening?.kvk_nummer && !watch('kvk_nummer')) {
      setValue('kvk_nummer', screeningData.data.screening.kvk_nummer);
    }
  }, [screeningData, setValue, watch]);

  // Auto-calculate KVK years when date changes
  const kvkRegistrationDate = watch('kvk_registration_date');
  React.useEffect(() => {
    if (kvkRegistrationDate) {
      const years = calculateYearsFromKVKDate(kvkRegistrationDate);
      setValue('kvk_registration_years', years);
      // Also update years_self_employed if it's 0 or not set
      const currentYears = watch('years_self_employed') || 0;
      if (currentYears === 0) {
        setValue('years_self_employed', years);
      }
    }
  }, [kvkRegistrationDate, setValue, watch]);

  // Field focusing function
  const handleFieldFocus = (fieldId: string, fieldType: string, data: any) => {
    console.log('🎯 Attempting to focus field:', fieldId, 'type:', fieldType);

    if (fieldType === 'work_experience_auto_add') {
      if (!data.work_experience || data.work_experience.length === 0) {
        addWorkExperience();
        setTimeout(() => {
          const element = document.getElementById('work_experience_company_name_0');
          if (element) {
            focusElement(element);
          }
        }, 200);
      }
    } else if (fieldType === 'education_auto_add') {
      if (!data.education || data.education.length === 0) {
        addEducation();
        setTimeout(() => {
          const element = document.getElementById('education_institution_0');
          if (element) {
            focusElement(element);
          }
        }, 200);
      }
    } else {
      // Regular field focusing
      let element = document.getElementById(fieldId) as HTMLElement;
      if (!element) {
        element = document.querySelector(`input[name="${fieldId}"], select[name="${fieldId}"], textarea[name="${fieldId}"]`) as HTMLElement;
      }
      if (element) {
        focusElement(element);
      } else {
        console.warn('❌ Could not find element:', fieldId);
      }
    }
  };

  // Helper function to focus and highlight an element
  const focusElement = (element: HTMLElement) => {
    element.focus();
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.5)';
    element.style.borderColor = '#3b82f6';
    setTimeout(() => {
      element.style.boxShadow = '';
      element.style.borderColor = '';
    }, 2000);
  };

  // Debug activeTab changes and handle field focusing after tab switch
  React.useEffect(() => {
    console.log('🔄 Active tab changed to:', activeTab);

    // Check if we have a pending field focus operation
    const pendingFocus = sessionStorage.getItem('pendingFieldFocus');
    if (pendingFocus) {
      const focusData = JSON.parse(pendingFocus);
      console.log('🎯 Processing pending focus:', focusData);

      // Clear the pending focus
      sessionStorage.removeItem('pendingFieldFocus');

      // Give React time to render the new tab content
      setTimeout(() => {
        handleFieldFocus(focusData.fieldId, focusData.fieldType, focusData.data);
      }, 100);
    }
    // Alleen bij tabwissel; handleFieldFocus leest geen state die hier verouderd kan zijn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Watch values for dependent fields and re-renders
  const availabilityStatus = watch('availability_status');
  const workExperience = watch('work_experience') || [];
  const education = watch('education') || [];

  // Get professional types from screening
  const selectedProfessionalTypes = React.useMemo(() => {
    // First try to get from profile (from screening approval)
    if (profileStatus?.data?.profile?.professional_types_from_screening) {
      try {
        const types = profileStatus.data.profile.professional_types_from_screening;
        return Array.isArray(types) ? types : JSON.parse(types);
      } catch {
        // Fall through to screening data
      }
    }
    // Fallback to screening data
    if (screeningData?.data?.screening?.selected_professional_types) {
      try {
        const types = screeningData.data.screening.selected_professional_types;
        return Array.isArray(types) ? types : JSON.parse(types);
      } catch {
        return [];
      }
    }
    return [];
  }, [screeningData, profileStatus]);

  // Calculate years from KVK registration date
  const calculateYearsFromKVKDate = (kvkDate: string | null | undefined): number => {
    if (!kvkDate) return 0;
    const registrationDate = new Date(kvkDate);
    const today = new Date();
    let years = today.getFullYear() - registrationDate.getFullYear();
    const monthDiff = today.getMonth() - registrationDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < registrationDate.getDate())) {
      years--;
    }
    return Math.max(0, years);
  };

  // Experience level from years - matches backend: 0-2, 2-5, 5-10, 10+
  const getExperienceLevelFromYears = (years: number): '0-2' | '2-5' | '5-10' | '10+' => {
    if (years >= 10) return '10+';
    if (years >= 5) return '5-10';
    if (years >= 2) return '2-5';
    return '0-2';
  };

  const EXPERIENCE_LEVEL_LABELS: Record<string, string> = {
    '0-2': t('belagom:constant.experienceLevel.starter'),
    '2-5': t('belagom:constant.experienceLevel.experienced'),
    '5-10': t('belagom:constant.experienceLevel.advanced'),
    '10+': t('belagom:constant.experienceLevel.expert')
  };

  // Fetch rate structures from API (matches document)
  const { data: groupedRatesData } = useQuery({
    queryKey: ['professional-rates-grouped'],
    queryFn: async () => {
      const response = await api.get('/professional-rates/grouped');
      return response.data.data;
    },
    enabled: selectedProfessionalTypes.length > 0
  });

  // Build allRates from API data per professional type and experience level
  const kvkDate = watch('kvk_registration_date');
  const calculatedYears = kvkDate ? calculateYearsFromKVKDate(kvkDate) : 0;
  const experienceLevel = getExperienceLevelFromYears(calculatedYears);

  const allRates = React.useMemo(() => {
    if (!groupedRatesData || selectedProfessionalTypes.length === 0 || !kvkDate) return [];
    return selectedProfessionalTypes
      .map((type: string) => {
        const typeData = groupedRatesData.find((g: { type: string }) => g.type === type);
        if (!typeData?.rates) return null;
        const rate = typeData.rates.find((r: { experience_level: string }) => r.experience_level === experienceLevel);
        if (!rate) return null;
        const baseRate = Number(rate.base_rate);
        const finalRate = Number(rate.final_rate);
        const belagomAmount = finalRate - baseRate;
        return {
          type,
          experienceLevel,
          yourRate: baseRate.toFixed(2),
          belagom: belagomAmount.toFixed(2),
          max: finalRate.toFixed(2)
        };
      })
      .filter(Boolean) as Array<{
        type: string;
        experienceLevel: string;
        yourRate: string;
        belagom: string;
        max: string;
      }>;
  }, [groupedRatesData, selectedProfessionalTypes, experienceLevel, kvkDate]);

  // Note: allLanguages, selectedLanguages, and availableLanguages are already declared at the top of the component

  // Handle profile photo upload
  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error(t('toast.photoTooLarge'));
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setProfilePhoto(reader.result as string);
        setValue('profile_photo', file);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle intro video upload
  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      toast.error(t('toast.videoTooLarge'));
      return;
    }

    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('toast.invalidVideoFormat'));
      return;
    }

    setVideoUploading(true);
    try {
      const formData = new FormData();
      formData.append('intro_video', file);

      const response = await api.post('/professional/intro-video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const videoPath = response.data.data.intro_video_path;
        setIntroVideo(`/uploads/${videoPath}`);
        toast.success(t('toast.videoUploaded'));
      }
    } catch (error: any) {
      console.error('Video upload error:', error);
      toast.error(error?.response?.data?.message || t('toast.videoUploadError'));
    } finally {
      setVideoUploading(false);
    }
  };

  // Handle intro video deletion
  const handleVideoDelete = async () => {
    try {
      await api.delete('/professional/intro-video');
      setIntroVideo(null);
      toast.success(t('toast.videoDeleted'));
    } catch (error) {
      console.error('Video delete error:', error);
      toast.error(t('toast.videoDeleteError'));
    }
  };

  // Add work experience
  const addWorkExperience = () => {
    const currentExperience = getValues('work_experience') || [];
    setValue('work_experience', [...currentExperience, {
      company: '',
      position: '',
      start_date: '',
      end_date: '',
      current: false,
      description: ''
    }]);
  };

  // Remove work experience
  const removeWorkExperience = (index: number) => {
    const currentExperience = getValues('work_experience') || [];
    const updated = currentExperience.filter((_, i) => i !== index);
    setValue('work_experience', updated);
  };

  // Add education
  const addEducation = () => {
    const currentEducation = getValues('education') || [];
    setValue('education', [...currentEducation, {
      institution: '',
      degree: '',
      field: '',
      start_year: new Date().getFullYear(),
      end_year: undefined,
      current: false
    }]);
  };

  // --- LinkedIn import helpers (CSV / pasted text) ---
  const parseCSV = (text: string) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
    if (lines.length === 0) return { work: [], edu: [] };
    const header = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
    const rows = lines.slice(1).map(l => {
      // naive CSV split (handles simple cases)
      const cols = l.split(',').map(c => c.replace(/"/g, '').trim());
      const obj: Record<string, string> = {};
      header.forEach((h, i) => { obj[h] = cols[i] || ''; });
      return obj;
    });

    const work: any[] = [];
    const edu: any[] = [];

    rows.forEach(r => {
      const keys = Object.keys(r);
      // detect work row heuristics
      if (keys.some(k => k.includes('company') || k.includes('employer') || k.includes('organization')) || keys.some(k => k.includes('title') || k.includes('position'))) {
        const company = r['company'] || r['employer'] || r['organization'] || r['company name'] || '';
        const position = r['title'] || r['position'] || r['role'] || '';
        const start = r['start date'] || r['start'] || r['from'] || '';
        const end = r['end date'] || r['end'] || r['to'] || '';
        const description = r['description'] || r['summary'] || '';
        if (company || position) {
          work.push({ company, position, start_date: start, end_date: end, current: !end, description: description || `${position} bij ${company}` });
        }
      }

      // detect education row heuristics
      if (keys.some(k => k.includes('school') || k.includes('institution') || k.includes('degree') || k.includes('field'))) {
        const institution = r['school'] || r['institution'] || r['school name'] || '';
        const degree = r['degree'] || r['credential'] || '';
        const field = r['field of study'] || r['field'] || r['major'] || '';
        const start = r['start date'] || r['start'] || '';
        const end = r['end date'] || r['end'] || '';
        if (institution || degree) {
          edu.push({ institution, degree, field, start_year: start ? parseInt(start.slice(0,4)) || new Date().getFullYear() : new Date().getFullYear(), end_year: end ? parseInt(end.slice(0,4)) || undefined : undefined, current: !end });
        }
      }
    });

    return { work, edu };
  };

  const parsePasted = (text: string) => {
    const blocks = text.split(/\n\n+/).map(b => b.trim()).filter(Boolean);
    const work: any[] = [];
    const edu: any[] = [];

    blocks.forEach(b => {
      // common pattern: "Position\nCompany\nDates\nDescription"
      const lines = b.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      if (lines.length >= 2) {
        // try to find a date line
        const dateLineIndex = lines.findIndex(l => /\d{4}/.test(l));
        let dates = '';
        if (dateLineIndex !== -1) dates = lines[dateLineIndex];

        // heuristics: if first line contains ' at ' or second line contains company-like tokens
        if (/ at |@/i.test(lines[0]) || lines[1].length > 0) {
          // parse "Role at Company" or "Company - Role"
          let position = lines[0];
          let company = lines[1];
          const atMatch = lines[0].match(/(.+) at (.+)/i);
          if (atMatch) { position = atMatch[1].trim(); company = atMatch[2].trim(); }
          // dates parsing
          let start = '';
          let end = '';
          if (dates) {
            const matches = dates.match(/(\d{4})/g);
            if (matches && matches.length > 0) start = matches[0];
            if (matches && matches.length > 1) end = matches[1];
          }
          work.push({ company, position, start_date: start ? `${start}-01-01` : '', end_date: end ? `${end}-12-31` : '', current: !end, description: lines.slice(dateLineIndex === -1 ? 2 : dateLineIndex + 1).join('\n') });
          return;
        }

        // education heuristic: lines with degree keywords
        if (/degree|bachelor|master|msc|ba|ma|phd|university|college/i.test(b)) {
          const institution = lines[0];
          const degree = lines[1] || '';
          const yearMatch = b.match(/(19|20)\d{2}/g);
          const start = yearMatch?.[0] || new Date().getFullYear();
          const end = yearMatch?.[1];
          edu.push({ institution, degree, field: '', start_year: parseInt(String(start)), end_year: end ? parseInt(String(end)) : undefined, current: !end });
          return;
        }
      }
    });

    return { work, edu };
  };

  const handleCSVUpload = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target?.result || '');
      const { work, edu } = parseCSV(text);
      if (work.length > 0) setValue('work_experience', work);
      if (edu.length > 0) setValue('education', edu);
      if (work.length || edu.length) {
        toast.success(t('toast.csvImported'));
        setActiveTab('experience');
      } else {
        toast.error(t('toast.csvParseError'));
      }
    };
    reader.readAsText(file, 'utf-8');
  };

  const handlePasteImport = (text: string) => {
    const { work, edu } = parsePasted(text);
    if (work.length > 0) setValue('work_experience', work);
    if (edu.length > 0) setValue('education', edu);
    if (work.length || edu.length) {
      toast.success(t('toast.textImported'));
      setActiveTab('experience');
    } else {
      toast.error(t('toast.textParseError'));
    }
  };

  // Remove education
  const removeEducation = (index: number) => {
    const currentEducation = getValues('education') || [];
    const updated = currentEducation.filter((_, i) => i !== index);
    setValue('education', updated);
  };

  const onSubmit = async (data: ProfileData) => {
    console.log('onSubmit called with data:', data);
    // Clear validation errors when submitting
    setValidationErrors({});
    setIsLoading(true);

    console.log('Submit data:', data);
    console.log('Work experience:', data.work_experience);
    console.log('Education:', data.education);

    try {
      // Manual validation check since React Hook Form validation might not trigger for arrays
      const errors = [];
      let firstErrorField = null;
      let firstErrorTab = null;

      // Check required personal fields
      if (!data.first_name) {
        errors.push(t('label.firstName').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'first_name'; firstErrorTab = 'personal'; }
      }
      if (!data.last_name) {
        errors.push(t('label.lastName').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'last_name'; firstErrorTab = 'personal'; }
      }
      if (!data.birth_date) {
        errors.push(t('label.birthDate').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'birth_date'; firstErrorTab = 'personal'; }
      }
      if (!data.phone) {
        errors.push(t('label.phone').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'phone'; firstErrorTab = 'personal'; }
      }
      if (!data.email) {
        errors.push(t('label.email').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'email'; firstErrorTab = 'personal'; }
      }
      if (!data.street_address) {
        errors.push(t('label.street').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'street_address'; firstErrorTab = 'personal'; }
      }
      if (!data.postal_code) {
        errors.push(t('label.postalCode').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'postal_code'; firstErrorTab = 'personal'; }
      }
      if (!data.city) {
        errors.push(t('label.city').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'city'; firstErrorTab = 'personal'; }
      }

      // Gesplitste bio (FO stap 3)
      if (!data.bio_medewerker) {
        errors.push(t('label.bioMedewerker').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'bio_medewerker'; firstErrorTab = 'personal'; }
      }
      if (!data.bio_werkgever) {
        errors.push(t('label.bioWerkgever').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'bio_werkgever'; firstErrorTab = 'personal'; }
      }

      // Expertise- en doelgroeptags (FO stap 4)
      if (!data.specialisatie_tags || data.specialisatie_tags.length === 0) {
        errors.push(t('label.specialisations').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'specialisatie_tags'; firstErrorTab = 'professional'; }
      }
      if (!data.doelgroep_niveaus || data.doelgroep_niveaus.length === 0) {
        errors.push(t('label.targetGroups').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'doelgroep_niveaus'; firstErrorTab = 'professional'; }
      }
      if (!data.affinity || data.affinity.length === 0) {
        errors.push(t('label.industries').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'affinity'; firstErrorTab = 'professional'; }
      }

      // Check professional fields
      if (data.years_self_employed === undefined || data.years_self_employed === null) {
        errors.push(t('label.kvkYears'));
        if (!firstErrorField) { firstErrorField = 'years_self_employed'; firstErrorTab = 'professional'; }
      }
      // hourly_rate removed - rates are determined automatically

      // Check availability fields
      if (!data.availability_status) {
        errors.push(t('label.availability').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'availability_status'; firstErrorTab = 'availability'; }
      }
      // Wet DBA: geen urenvraag meer — minimaal aantal geopende tijdslots (FO stap 9)
      if (availabilitySlots.length < minTijdslots) {
        errors.push(t('label.timeslots').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'availability_slots'; firstErrorTab = 'availability'; }
      }
      if (!data.languages || data.languages.length === 0) {
        errors.push(t('label.languages').replace(' *', ''));
        if (!firstErrorField) { firstErrorField = 'languages'; firstErrorTab = 'availability'; }
      }
      // Provincie-selectie vervangen door reisradius — geen verplichte regio's meer

      // Check work experience
      if (!data.work_experience || data.work_experience.length === 0) {
        errors.push(t('label.workExperienceItem'));
        if (!firstErrorField) { firstErrorField = 'work_experience'; firstErrorTab = 'experience'; }
      } else {
        // Check if any work experience entries are incomplete
        for (let i = 0; i < data.work_experience.length; i++) {
          const exp = data.work_experience[i];
          if (!exp.company) {
            errors.push(`${t('label.company').replace(' *', '')} (${t('label.workExperienceItem')} ${i + 1})`);
            if (!firstErrorField) { firstErrorField = `work_experience_company_name_${i}`; firstErrorTab = 'experience'; }
            break;
          } else if (!exp.position) {
            errors.push(`${t('label.position').replace(' *', '')} (${t('label.workExperienceItem')} ${i + 1})`);
            if (!firstErrorField) { firstErrorField = `work_experience_role_${i}`; firstErrorTab = 'experience'; }
            break;
          } else if (!exp.description || exp.description.trim().length < 10) {
            errors.push(`${t('label.description').replace(' *', '')} (${t('label.workExperienceItem')} ${i + 1})`);
            if (!firstErrorField) { firstErrorField = `work_experience_description_${i}`; firstErrorTab = 'experience'; }
            break;
          }
        }
      }

      // Check education
      if (!data.education || data.education.length === 0) {
        errors.push(t('label.educationItem'));
        if (!firstErrorField) { firstErrorField = 'education'; firstErrorTab = 'experience'; }
      } else {
        // Check if any education entries are incomplete
        for (let i = 0; i < data.education.length; i++) {
          const edu = data.education[i];
          if (!edu.institution) {
            errors.push(`${t('label.institution').replace(' *', '')} (${t('label.educationItem')} ${i + 1})`);
            if (!firstErrorField) { firstErrorField = `education_institution_${i}`; firstErrorTab = 'experience'; }
            break;
          } else if (!edu.degree) {
            errors.push(`${t('label.degree').replace(' *', '')} (${t('label.educationItem')} ${i + 1})`);
            if (!firstErrorField) { firstErrorField = `education_degree_${i}`; firstErrorTab = 'experience'; }
            break;
          } else if (!edu.field) {
            errors.push(`${t('label.fieldOfStudy').replace(' *', '')} (${t('label.educationItem')} ${i + 1})`);
            if (!firstErrorField) { firstErrorField = `education_field_${i}`; firstErrorTab = 'experience'; }
            break;
          }
        }
      }

      console.log('Validation errors:', errors);
      console.log('First error field:', firstErrorField, 'tab:', firstErrorTab);

      if (errors.length > 0) {
        console.log('🔍 Debug info:');
        console.log('- Errors found:', errors);
        console.log('- First error field:', firstErrorField);
        console.log('- First error tab:', firstErrorTab);
        console.log('- Current active tab:', activeTab);

        toast.error(`${t('toast.validationError')}: ${errors.slice(0, 3).join(', ')}${errors.length > 3 ? ' ...' : ''}`);

        // Navigate to first incomplete tab
        if (firstErrorTab) {
          console.log('🔄 Switching to tab:', firstErrorTab);

          // Store field focus info for after tab switch
          if (firstErrorField === 'work_experience') {
            sessionStorage.setItem('pendingFieldFocus', JSON.stringify({
              fieldId: 'work_experience_company_name_0',
              fieldType: 'work_experience_auto_add',
              data
            }));
          } else if (firstErrorField === 'education') {
            sessionStorage.setItem('pendingFieldFocus', JSON.stringify({
              fieldId: 'education_institution_0',
              fieldType: 'education_auto_add',
              data
            }));
          } else if (firstErrorField) {
            sessionStorage.setItem('pendingFieldFocus', JSON.stringify({
              fieldId: firstErrorField,
              fieldType: 'regular',
              data
            }));
          }

          // Use functional setState to ensure we get the latest state
          setActiveTab(prevTab => {
            console.log('🔄 Tab state change:', prevTab, '->', firstErrorTab);
            return firstErrorTab;
          });

          // Force a re-render to ensure tab content is updated
          triggerRerender();
        }

        setIsLoading(false);
        return;
      }

      // Prepare profile data for API
      const profileData = {
        ...data,
        birth_date: data.birth_date || '', // Ensure birth_date is never undefined
        profile_photo: profilePhoto ? new File([profilePhoto], 'profile.jpg', { type: 'image/jpeg' }) : undefined,
        // Tijdslots gaan in dezelfde submit mee (FO stap 8)
        availability_slots: availabilitySlots,
      };

      console.log('Sending profile data to API:', profileData);

      await createProfile.mutateAsync(profileData);

      toast.success(t('toast.profileSaved'));

      // Mark profile as complete and refresh the publication checklist (FO stap 9);
      // publiceren is een aparte, bewuste actie van de professional (stap 10).
      localStorage.setItem('profile_complete', 'true');
      await Promise.all([refetchProfileStatus(), refetchPortfolioStatus()]);

    } catch (error: any) {
      console.error('Profile save error:', error);
      console.error('Error response:', error?.response?.data);
      console.error('Error status:', error?.response?.status);

      // Show detailed error message
      let errorMessage = t('toast.profileSaveError');

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        errorMessage = `Validatiefouten: ${error.response.data.errors.join(', ')}`;
      } else if (error?.response?.data?.errors?.[0]) {
        errorMessage = error.response.data.errors[0];
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, {
        duration: 10000,
      });

      // If there are error details, log them
      if (error?.response?.data?.errorDetails) {
        console.error('Validation error details:', error.response.data.errorDetails);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Check tab completion status
  const isTabComplete = (tabId: string) => {
    const formValues = getValues(); // Get current form values

    switch (tabId) {
      case 'personal':
        return Boolean(formValues.first_name && formValues.last_name && formValues.birth_date &&
               formValues.phone && formValues.email && formValues.street_address &&
               formValues.postal_code && formValues.city &&
               formValues.bio_medewerker && formValues.bio_werkgever);
      case 'professional':
        return formValues.years_self_employed !== undefined &&
               (formValues.specialisatie_tags || []).length > 0 &&
               (formValues.doelgroep_niveaus || []).length > 0 &&
               (formValues.affinity || []).length > 0;
      case 'availability':
        return Boolean(formValues.availability_status) &&
               availabilitySlots.length >= minTijdslots &&
               (formValues.languages || []).length > 0;
      case 'experience':
        return (formValues.work_experience || []).length > 0 && (formValues.education || []).length > 0;
      default:
        return false;
    }
  };

  const tabs = [
    { id: 'personal', name: t('tabs.personal'), icon: User },
    { id: 'professional', name: t('tabs.professional'), icon: Briefcase },
    { id: 'availability', name: t('tabs.availability'), icon: Clock },
    { id: 'experience', name: t('tabs.experience'), icon: GraduationCap }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {profileLoading ? (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('empty.loading')}</p>
          </div>
        </div>
      ) : (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('title')}</h1>
          <p className="mt-2 text-lg text-gray-600">
            {t('subtitle')}
          </p>
          {/* Member Number and Professional Types Display */}
          {(profileStatus?.data?.profile?.member_number || selectedProfessionalTypes.length > 0) && (
            <div className="mt-4 space-y-2">
              {profileStatus?.data?.profile?.member_number && (
                <div className="inline-block px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold text-blue-900">{t('memberNumber')}</span>{' '}
                    <span className="text-blue-700 font-mono">{profileStatus.data.profile.member_number}</span>
                  </p>
                </div>
              )}
              {selectedProfessionalTypes.length > 0 && (
                <div className="inline-block px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg ml-2">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold text-gray-900">{t('professionalTypes')}</span>{' '}
                    <span className="text-gray-700">
                      {selectedProfessionalTypes.map((type: string) => {
                        return t(`professional:roles.${type}`, { defaultValue: type });
                      }).join(', ')}
                    </span>
                    <span className="text-xs text-gray-500 ml-2">{t('fromScreening')}</span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit(onSubmit, (errors) => {
          console.log('Form validation errors:', errors);

          // Collect all error messages grouped by tab
          const errorsByTab: { [key: string]: string[] } = {
            personal: [],
            professional: [],
            availability: [],
            experience: []
          };

          // Helper to add error messages
          const addError = (fieldName: string, label: string, tab: string) => {
            if (errors[fieldName as keyof typeof errors]) {
              errorsByTab[tab].push(label);
            }
          };

          // Check all required fields and group by tab
          addError('first_name', t('label.firstName').replace(' *', ''), 'personal');
          addError('last_name', t('label.lastName').replace(' *', ''), 'personal');
          addError('birth_date', t('label.birthDate').replace(' *', ''), 'personal');
          addError('phone', t('label.phone').replace(' *', ''), 'personal');
          addError('email', t('label.email').replace(' *', ''), 'personal');
          addError('street_address', t('label.street').replace(' *', ''), 'personal');
          addError('postal_code', t('label.postalCode').replace(' *', ''), 'personal');
          addError('city', t('label.city').replace(' *', ''), 'personal');

          addError('bio_medewerker', t('label.bioMedewerker').replace(' *', ''), 'personal');
          addError('bio_werkgever', t('label.bioWerkgever').replace(' *', ''), 'personal');

          addError('years_self_employed', t('label.kvkYears'), 'professional');
          addError('specialisatie_tags', t('label.specialisations').replace(' *', ''), 'professional');
          addError('doelgroep_niveaus', t('label.targetGroups').replace(' *', ''), 'professional');
          addError('affinity', t('label.industries').replace(' *', ''), 'professional');

          addError('availability_status', t('label.availability').replace(' *', ''), 'availability');
          addError('languages', t('label.languages').replace(' *', ''), 'availability');

          addError('work_experience', t('label.workExperienceItem'), 'experience');
          addError('education', t('label.educationItem'), 'experience');

          // Collect all errors
          const allErrors: string[] = [];
          Object.values(errorsByTab).forEach(tabErrors => {
            allErrors.push(...tabErrors);
          });

          // Show detailed error message
          if (allErrors.length > 0) {
            // Store errors for display
            setValidationErrors(errorsByTab);

            // Find first tab with errors
            let firstErrorTab = 'personal';
            if (errorsByTab.personal.length > 0) firstErrorTab = 'personal';
            else if (errorsByTab.professional.length > 0) firstErrorTab = 'professional';
            else if (errorsByTab.availability.length > 0) firstErrorTab = 'availability';
            else if (errorsByTab.experience.length > 0) firstErrorTab = 'experience';

            // Switch to first error tab
            setActiveTab(firstErrorTab);

            // Build detailed error message
            let errorDetails = `${t('section.errorOverview')}:\n\n`;
            if (errorsByTab.personal.length > 0) {
              errorDetails += `${t('errorSection.personal')} ${errorsByTab.personal.join(', ')}\n`;
            }
            if (errorsByTab.professional.length > 0) {
              errorDetails += `${t('errorSection.professional')} ${errorsByTab.professional.join(', ')}\n`;
            }
            if (errorsByTab.availability.length > 0) {
              errorDetails += `${t('errorSection.availability')} ${errorsByTab.availability.join(', ')}\n`;
            }
            if (errorsByTab.experience.length > 0) {
              errorDetails += `${t('errorSection.experience')} ${errorsByTab.experience.join(', ')}\n`;
            }

            // Show toast with summary
            const summaryText = allErrors.length <= 5
              ? `${t('errorSection.fillFields')} ${allErrors.join(', ')}`
              : t('errorSection.missingFields', { count: allErrors.length });

            toast.error(summaryText, {
              duration: 8000,
            });

            // Log detailed errors to console
            console.error('Validatiefouten:', errorDetails);
            console.error('Alle ontbrekende velden:', allErrors);

            // Scroll to first error field after a short delay to allow tab switch
            setTimeout(() => {
              const firstErrorField = Object.keys(errors)[0];
              if (firstErrorField) {
                const element = document.querySelector(`[name="${firstErrorField}"]`) ||
                               document.querySelector(`#${firstErrorField}`) ||
                               document.querySelector(`[id*="${firstErrorField}"]`);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  (element as HTMLElement).focus();
                }
              }
            }, 300);
          } else {
            setValidationErrors({});
            toast.error(t('toast.validationError'));
          }
        })}>
          {/* Validation Errors Overview */}
          {(Object.values(validationErrors).flat().length > 0) && (
            <Card className="mb-6 border-2 border-red-300 bg-red-50">
              <div className="p-4">
                <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center">
                  <span className="mr-2">⚠️</span>
                  {t('errorSection.missingFields', { count: Object.values(validationErrors).flat().length })}
                </h3>
                <div className="space-y-2">
                  {validationErrors.personal && validationErrors.personal.length > 0 && (
                    <div>
                      <span className="font-medium text-red-700">{t('errorSection.personal')}</span>
                      <span className="ml-2 text-red-600">{validationErrors.personal.join(', ')}</span>
                      <button
                        type="button"
                        onClick={() => setActiveTab('personal')}
                        className="ml-2 text-blue-600 hover:text-blue-800 underline text-sm"
                      >
                        {t('button.goToTab')}
                      </button>
                    </div>
                  )}
                  {validationErrors.professional && validationErrors.professional.length > 0 && (
                    <div>
                      <span className="font-medium text-red-700">{t('errorSection.professional')}</span>
                      <span className="ml-2 text-red-600">{validationErrors.professional.join(', ')}</span>
                      <button
                        type="button"
                        onClick={() => setActiveTab('professional')}
                        className="ml-2 text-blue-600 hover:text-blue-800 underline text-sm"
                      >
                        {t('button.goToTab')}
                      </button>
                    </div>
                  )}
                  {validationErrors.availability && validationErrors.availability.length > 0 && (
                    <div>
                      <span className="font-medium text-red-700">{t('errorSection.availability')}</span>
                      <span className="ml-2 text-red-600">{validationErrors.availability.join(', ')}</span>
                      <button
                        type="button"
                        onClick={() => setActiveTab('availability')}
                        className="ml-2 text-blue-600 hover:text-blue-800 underline text-sm"
                      >
                        {t('button.goToTab')}
                      </button>
                    </div>
                  )}
                  {validationErrors.experience && validationErrors.experience.length > 0 && (
                    <div>
                      <span className="font-medium text-red-700">{t('errorSection.experience')}</span>
                      <span className="ml-2 text-red-600">{validationErrors.experience.join(', ')}</span>
                      <button
                        type="button"
                        onClick={() => setActiveTab('experience')}
                        className="ml-2 text-blue-600 hover:text-blue-800 underline text-sm"
                      >
                        {t('button.goToTab')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Tab Navigation */}
          <div className="mb-8">
            {/* Vijf tabs passen niet op telefoonbreedte: de balk scrolt horizontaal. */}
            <div className="border-b border-gray-200 overflow-x-auto overflow-y-hidden">
              <nav className="-mb-px flex min-w-max space-x-8">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isComplete = isTabComplete(tab.id);
                  const isActive = activeTab === tab.id;

                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm relative ${
                        isActive
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {tab.name}
                      {isComplete ? (
                        <span className="ml-2 text-green-500">✓</span>
                      ) : (
                        <span className="ml-2 text-red-500 font-bold">*</span>
                      )}
                    </button>
                  );
                })}
                {/* Facturen staat los van het profielformulier: geen verplichte velden, eigen opslaan. */}
                <button
                  type="button"
                  onClick={() => setActiveTab('facturen')}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'facturen'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Receipt className="w-4 h-4 mr-2" />
                  {tFacturen('tab')}
                </button>
              </nav>
            </div>
          </div>

          {/* Personal Information Tab */}
          {activeTab === 'personal' && (
            <Card className="mb-8">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  {t('section.personalInfo')}
                </h2>
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={openLinkedInPopup}
                    className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M4.98 3.5C4.98 4.88 3.86 6 2.49 6S0 4.88 0 3.5 1.12 1 2.49 1 4.98 2.12 4.98 3.5zM0 8h4.98v13H0V8zm7.5 0H12v1.86c.65-1.16 2.34-2.38 4.82-2.38 5.16 0 6.12 3.4 6.12 7.82V21H17.6v-6.46c0-1.54-.03-3.52-2.15-3.52-2.16 0-2.49 1.69-2.49 3.42V21H7.5V8z"/></svg>
                    {t('button.fillLinkedIn')}
                  </button>
                </div>

                {/* Profile Photo */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('section.profilePhoto')}
                  </label>
                  <div className="flex items-center space-x-4">
                    <BelagomProfilePhoto src={profilePhoto} size={80} />
                    <div>
                      <label className="cursor-pointer bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                        <Upload className="w-4 h-4 inline mr-2" />
                        {t('button.uploadPhoto')}
                        <input
                          type="file"
                          className="sr-only"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('info.photoFormats')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Intro Video */}
                <div className="mt-6 mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('section.introVideo')}
                  </label>
                  <p className="text-sm text-gray-600 mb-3">
                    {t('info.videoDesc')}
                  </p>
                  {introVideo ? (
                    <div className="space-y-3">
                      <video
                        src={introVideo}
                        controls
                        className="w-full max-w-md rounded-lg border border-gray-200"
                        style={{ maxHeight: '300px' }}
                      />
                      <div className="flex items-center space-x-3">
                        <label className="cursor-pointer bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                          <Upload className="w-4 h-4 inline mr-2" />
                          {t('button.replaceVideo')}
                          <input
                            type="file"
                            className="sr-only"
                            accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                            onChange={handleVideoUpload}
                            disabled={videoUploading}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={handleVideoDelete}
                          className="px-3 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 inline mr-2" />
                          {t('button.deleteVideo')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-4">
                      <label className={`cursor-pointer border-2 border-dashed border-gray-300 rounded-lg px-6 py-4 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors ${videoUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <Video className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                        <span className="text-sm font-medium text-gray-700">
                          {videoUploading ? t('common:common.loading') : t('button.uploadVideo')}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {t('info.videoFormats')}
                        </p>
                        <input
                          type="file"
                          className="sr-only"
                          accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                          onChange={handleVideoUpload}
                          disabled={videoUploading}
                        />
                      </label>
                    </div>
                  )}
                </div>

                {/* Alternatief voor de upload: YouTube/Vimeo-link (FO stap 7) */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('label.videoUrl')}
                  </label>
                  <input
                    type="url"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://www.youtube.com/watch?v=..."
                    {...register('intro_video_url')}
                  />
                  {errors.intro_video_url && (
                    <p className="mt-1 text-sm text-red-600">{errors.intro_video_url.message}</p>
                  )}
                </div>

                {/* Professional Introduction */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('section.introduction')}
                  </label>
                  <div className="relative">
                    <textarea
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('placeholder.introduction')}
                      {...register('introduction')}
                      maxLength={750}
                    />
                    <div className="absolute bottom-2 right-2 text-sm text-gray-500">
                      {watch('introduction')?.length || 0}/750
                    </div>
                  </div>
                  {errors.introduction && (
                    <p className="mt-1 text-sm text-red-600">{errors.introduction.message}</p>
                  )}
                {/* Gesplitste bio (FO stap 3): één tekst voor de medewerker,
                    één voor de HR-manager — bewust twee aparte velden. */}
                <div className="mt-6" id="bio_medewerker">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('label.bioMedewerker')}
                  </label>
                  <p className="text-sm text-gray-600 mb-2">{t('info.bioMedewerkerDesc')}</p>
                  <textarea
                    rows={5}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('placeholder.bioMedewerker')}
                    {...register('bio_medewerker')}
                  />
                  <div className="mt-1 flex justify-between text-sm">
                    <span className={errors.bio_medewerker ? 'text-red-600' : 'text-transparent'}>
                      {errors.bio_medewerker?.message || '.'}
                    </span>
                    <span className={countWords(watch('bio_medewerker')) > MAX_BIO_WOORDEN ? 'text-red-600' : 'text-gray-500'}>
                      {countWords(watch('bio_medewerker'))}/{MAX_BIO_WOORDEN}
                    </span>
                  </div>
                </div>

                <div className="mt-4" id="bio_werkgever">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('label.bioWerkgever')}
                  </label>
                  <p className="text-sm text-gray-600 mb-2">{t('info.bioWerkgeverDesc')}</p>
                  <textarea
                    rows={5}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('placeholder.bioWerkgever')}
                    {...register('bio_werkgever')}
                  />
                  <div className="mt-1 flex justify-between text-sm">
                    <span className={errors.bio_werkgever ? 'text-red-600' : 'text-transparent'}>
                      {errors.bio_werkgever?.message || '.'}
                    </span>
                    <span className={countWords(watch('bio_werkgever')) > MAX_BIO_WOORDEN ? 'text-red-600' : 'text-gray-500'}>
                      {countWords(watch('bio_werkgever'))}/{MAX_BIO_WOORDEN}
                    </span>
                  </div>
                </div>

                  <div className="mt-2 text-sm text-gray-600">
                    <p>{t('info.introSuggestions')}</p>
                    <ul className="list-disc list-inside ml-4 mt-1">
                      <li>{t('info.suggestion1')}</li>
                      <li>{t('info.suggestion2')}</li>
                      <li>{t('info.suggestion3')}</li>
                    </ul>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('label.firstName')}
                    </label>
                    <input
                      id="first_name"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      {...register('first_name')}
                    />
                    {errors.first_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.first_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('label.lastName')}
                    </label>
                    <input
                      id="last_name"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      {...register('last_name')}
                    />
                    {errors.last_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.last_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('label.birthDate')}
                    </label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      {...register('birth_date')}
                    />
                    {errors.birth_date && (
                      <p className="mt-1 text-sm text-red-600">{errors.birth_date.message}</p>
                    )}
                  </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('label.phone')}
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    {...register('phone')}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                  )}
                </div>                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('label.email')}
                    </label>
                    <input
                      type="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      {...register('email')}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                {/* Address */}
                <h3 className="text-lg font-medium text-gray-900 mt-8 mb-4">{t('section.address')}</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('label.street')}
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      {...register('street_address')}
                    />
                    {errors.street_address && (
                      <p className="mt-1 text-sm text-red-600">{errors.street_address.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('label.postalCode')}
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      {...register('postal_code')}
                    />
                    {errors.postal_code && (
                      <p className="mt-1 text-sm text-red-600">{errors.postal_code.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('label.city')}
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      {...register('city')}
                    />
                    {errors.city && (
                      <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Professional Tab */}
          {activeTab === 'professional' && (
            <Card className="mb-8">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  {t('section.professionalData')}
                </h2>

                {/* KVK Information - for tariff determination only */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-900 mb-4">
                    {t('section.kvkInfo')}
                  </h3>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('label.kvkNumber')}
                      </label>
                      <input
                        type="text"
                        maxLength={8}
                        placeholder={t('placeholder.kvkNumber')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        {...register('kvk_nummer')}
                      />
                      {errors.kvk_nummer && (
                        <p className="mt-1 text-sm text-red-600">{errors.kvk_nummer.message}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        {t('info.kvkOnlyForRate')}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('label.kvkRegDate')}
                      </label>
                      <input
                        type="date"
                        max={new Date().toISOString().split('T')[0]}
                        readOnly={!!(screeningData?.data?.screening?.opgericht_datum || profileStatus?.data?.profile?.kvk_registration_date)}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                          (screeningData?.data?.screening?.opgericht_datum || profileStatus?.data?.profile?.kvk_registration_date)
                            ? 'bg-gray-100 cursor-not-allowed'
                            : ''
                        }`}
                        {...register('kvk_registration_date')}
                      />
                      {errors.kvk_registration_date && (
                        <p className="mt-1 text-sm text-red-600">{errors.kvk_registration_date.message}</p>
                      )}
                      {(screeningData?.data?.screening?.opgericht_datum || profileStatus?.data?.profile?.kvk_registration_date) && (
                        <p className="mt-1 text-xs text-gray-500 italic">
                          {t('info.kvkRegReadonly')}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('label.kvkYears')}
                      </label>
                      <input
                        type="text"
                        readOnly
                        value={calculatedYears > 0 ? `${calculatedYears} jaar` : t('info.kvkYearsNotCalc')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                      />
                      <p className="mt-1 text-xs text-gray-500 italic">
                        {t('info.kvkYearsReadonly')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Rate Information - per professional type */}
                {selectedProfessionalTypes.length > 0 && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h3 className="text-sm font-medium text-blue-900 mb-4">
                      {t('section.rateStructure')} ({calculatedYears > 0 ? `${calculatedYears} jaar ervaring` : 'Geen KVK datum'})
                    </h3>

                    <div className="mb-4 p-3 bg-amber-50 rounded-md border border-amber-200">
                      <p className="text-sm text-amber-900">
                        <strong>{t('info.rateFreedomTitle')}</strong> {t('info.rateFreedomNote')}
                      </p>
                    </div>

                    <p className="text-sm text-blue-800 mb-4">
                      <strong>{t('label.yourFunctions')}</strong> {selectedProfessionalTypes.map((tp: string) => {
                        return t(`professional:roles.${tp}`, { defaultValue: tp });
                      }).join(', ')}
                    </p>

                    {allRates.length > 0 ? (
                      <div className="space-y-4">
                        {allRates.map((rate, index) => (
                          <div key={index} className="bg-white p-4 rounded-md border border-blue-200">
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">
                              {t(`professional:roles.${rate.type}`, { defaultValue: rate.type })} - {EXPERIENCE_LEVEL_LABELS[rate.experienceLevel] || rate.experienceLevel}
                            </h4>

                            <div className="grid md:grid-cols-3 gap-4 text-sm">
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-gray-600 block mb-1">{t('label.yourRate')}</span>
                                <span className="text-2xl font-bold text-green-600">€{rate.yourRate}</span>
                                <span className="text-gray-500 text-xs block mt-1">{t('label.whatYouReceive')}</span>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-gray-600 block mb-1">{t('label.belagomMarkup')}</span>
                                <span className="text-2xl font-bold text-blue-600">€{rate.belagom}</span>
                                <span className="text-gray-500 text-xs block mt-1">{t('label.platformFee')}</span>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-md">
                                <span className="text-gray-600 block mb-1">{t('label.clientPays')}</span>
                                <span className="text-2xl font-bold text-gray-900">€{rate.max}</span>
                                <span className="text-gray-500 text-xs block mt-1">{t('label.totalRate')}</span>
                              </div>
                            </div>

                            <p className="mt-3 text-xs text-gray-600 italic">
                              {t('info.ratePerAssignment')}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-yellow-50 p-3 rounded-md border border-yellow-200">
                        <p className="text-sm text-yellow-800">
                          {t('info.kvkRateHint')}
                        </p>
                      </div>
                    )}

                    <div className="mt-4 p-3 bg-white rounded-md border border-blue-200">
                      <p className="text-xs text-gray-600">
                        {t('info.rateLevels')}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('info.rateAutoAdjust')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Specialisme-tags (FO stap 4) — voeden de HR-zoekmachine */}
                <div className="mt-6" id="specialisatie_tags">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('label.specialisations')}
                  </label>
                  <p className="text-sm text-gray-600 mb-3">{t('info.specialisationsDesc')}</p>
                  <TagSelector
                    options={taxonomy?.specialisatie_tags || []}
                    value={watch('specialisatie_tags') || []}
                    onChange={(value) => setValue('specialisatie_tags', value, { shouldDirty: true, shouldValidate: true })}
                    max={maxSpecialisatieTags}
                    placeholder={t('placeholder.selectSpecialisation')}
                  />
                  {errors.specialisatie_tags && (
                    <p className="mt-1 text-sm text-red-600">{errors.specialisatie_tags.message}</p>
                  )}
                </div>

                {/* Doelgroep-check: ervaringsniveaus (FO stap 4) */}
                <div className="mt-6" id="doelgroep_niveaus">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('label.targetGroups')}
                  </label>
                  <p className="text-sm text-gray-600 mb-3">{t('info.targetGroupsDesc')}</p>
                  <TagSelector
                    options={taxonomy?.doelgroep_niveaus || []}
                    value={watch('doelgroep_niveaus') || []}
                    onChange={(value) => setValue('doelgroep_niveaus', value, { shouldDirty: true, shouldValidate: true })}
                    variant="checkbox"
                  />
                  {errors.doelgroep_niveaus && (
                    <p className="mt-1 text-sm text-red-600">{errors.doelgroep_niveaus.message}</p>
                  )}
                </div>

                {/* Branche-affiniteit (FO stap 5) */}
                <div className="mt-6" id="affinity">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('label.industries')}
                  </label>
                  <p className="text-sm text-gray-600 mb-3">{t('info.industriesDesc')}</p>
                  <TagSelector
                    options={taxonomy?.branche_affiniteiten || []}
                    value={watch('affinity') || []}
                    onChange={(value) => setValue('affinity', value, { shouldDirty: true, shouldValidate: true })}
                    variant="checkbox"
                  />
                  {errors.affinity && (
                    <p className="mt-1 text-sm text-red-600">{errors.affinity.message}</p>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Availability Tab */}
          {activeTab === 'availability' && (
            <Card className="mb-8">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  {t('section.availabilityLocation')}
                </h2>

                {/* Availability Status */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t('label.availability')}
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="not_available"
                        className="text-blue-600 border-gray-300 focus:ring-blue-500 mr-3"
                        {...register('availability_status')}
                      />
                      <span>{t('availability.notAvailable')}</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="immediately"
                        className="text-blue-600 border-gray-300 focus:ring-blue-500 mr-3"
                        {...register('availability_status')}
                      />
                      <span>{t('availability.immediately')}</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="specific_date"
                        className="text-blue-600 border-gray-300 focus:ring-blue-500 mr-3"
                        {...register('availability_status')}
                      />
                      <span>{t('availability.fromDate')}</span>
                    </label>
                  </div>
                </div>

                {availabilityStatus === 'specific_date' && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('label.availableFrom')}
                    </label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      {...register('available_from_date')}
                    />
                  </div>
                )}

                {/* Dubbele status-grendel: externe vindbaarheid (migratie 111) */}
                <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">
                    {t('grendel.title', { defaultValue: 'Vindbaarheid voor nieuwe opdrachtgevers' })}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {t('grendel.desc', { defaultValue: 'Sta je op "uit" of zit je aan je maximum aantal trajecten, dan ben je onzichtbaar in de zoekmachine voor nieuwe opdrachtgevers. Binnen je bestaande pools blijf je gewoon bereikbaar.' })}
                  </p>
                  <label className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      className="text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                      {...register('accepteer_nieuwe_pools')}
                    />
                    <span className="text-sm text-gray-700">
                      {t('grendel.acceptNewPools', { defaultValue: 'Ik accepteer uitnodigingen van nieuwe opdrachtgevers' })}
                    </span>
                  </label>
                  <label className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      className="text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                      {...register('werkt_alleen_online')}
                    />
                    <span className="text-sm text-gray-700">
                      {t('grendel.alleenOnline', { defaultValue: 'Ik werk 100% online (videoconsult) — status wordt blauw, geen reiskosten' })}
                    </span>
                  </label>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('grendel.maxTrajecten', { defaultValue: 'Maximum gelijktijdig lopende trajecten' })}
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    {...register('max_actieve_trajecten', { valueAsNumber: true })}
                  />
                  {errors.max_actieve_trajecten && (
                    <p className="mt-1 text-sm text-red-600">
                      {t('grendel.maxTrajectenError', { defaultValue: 'Kies een aantal tussen 1 en 50' })}
                    </p>
                  )}
                </div>

                {/* Tijdslots — vervangt 'uren per week' (Wet DBA, FO stap 8) */}
                <div className="mb-6" id="availability_slots">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('label.timeslots')}
                  </label>
                  <p className="text-sm text-gray-600 mb-4">
                    {t('info.timeslotsDesc')}
                  </p>
                  <TijdslotAgenda
                    slots={availabilitySlots}
                    onChange={setAvailabilitySlots}
                    minSlots={minTijdslots}
                  />
                </div>

                {/* Dynamische Reisradius — vervangt de provincie-selectie */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('reisradius.title')}
                  </label>
                  <p className="text-sm text-gray-600 mb-4">
                    {t('reisradius.description')}
                  </p>
                  <ReisradiusMap
                    radiusKm={watch('reisradius_km') ?? null}
                    onRadiusChange={(km) => setValue('reisradius_km', km, { shouldDirty: true })}
                  />
                </div>

                {/* Languages */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t('label.languages')}
                  </label>
                  <p className="text-sm text-gray-600 mb-3">
                    {t('info.languagesDefault')}
                  </p>

                  {/* Selected Languages */}
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-2">
                      {selectedLanguages.map((language) => (
                        <span
                          key={language}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                        >
                          {language}
                          {language !== (allLanguages[0] || 'Nederlands') && (
                            <button
                              type="button"
                              onClick={() => {
                                const newSelected = selectedLanguages.filter(l => l !== language);
                                setSelectedLanguages(newSelected);
                                setAvailableLanguages([...availableLanguages, language].sort());
                                setValue('languages', newSelected);
                              }}
                              className="ml-2 text-blue-600 hover:text-blue-800"
                            >
                              ×
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Add Language Button */}
                  {selectedLanguages.length < 5 && availableLanguages.length > 0 && (
                    <div className="mb-3">
                      <select
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            const newSelected = [...selectedLanguages, e.target.value];
                            setSelectedLanguages(newSelected);
                            setAvailableLanguages(availableLanguages.filter(l => l !== e.target.value));
                            setValue('languages', newSelected);
                          }
                        }}
                      >
                        <option value="">{t('button.addLanguage')}</option>
                        {availableLanguages.map(lang => (
                          <option key={lang} value={lang}>{lang}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {selectedLanguages.length >= 5 && (
                    <p className="text-sm text-gray-500">{t('empty.maxLanguages')}</p>
                  )}

                  {/* Hidden input for form validation */}
                  <input type="hidden" {...register('languages')} value={JSON.stringify(selectedLanguages)} />

                  {errors.languages && (
                    <p className="mt-1 text-sm text-red-600">{errors.languages.message}</p>
                  )}
                </div>

                {/* Language Knowledge Explanation */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('label.languageNote')}
                  </label>
                  <textarea
                    {...register('language_knowledge_explanation')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('placeholder.languageNote')}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    {t('info.languageNoteDesc')}
                  </p>
                </div>

                {/* Show employers on profile toggle */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3 mt-1"
                      {...register('show_employers_on_profile')}
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        {t('label.showEmployers')}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('info.showEmployersDesc')}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </Card>
          )}

          {/* Experience Tab */}
          {activeTab === 'experience' && (
            <Card className="mb-8">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  {t('section.workExperienceEducation')}
                </h2>

                {/* Work Experience */}
                <div className="mb-8" id="work-experience-section">
                  {/* LinkedIn import helper */}
                  <div className="mb-6 p-4 bg-white border border-gray-200 rounded-md">
                    <h4 className="text-sm font-medium mb-2">{t('section.importLinkedIn')}</h4>
                    <p className="text-sm text-gray-600 mb-3">{t('info.importDesc')}</p>
                    <div className="flex items-center space-x-3 mb-3">
                      <label className="cursor-pointer bg-blue-600 text-white px-3 py-2 rounded-md text-sm">
                        {t('button.chooseCSV')}
                        <input type="file" accept=".csv,text/csv" className="sr-only" onChange={(e) => handleCSVUpload(e.target.files?.[0] || null)} />
                      </label>
                      <button type="button" onClick={() => { const text = prompt(t('placeholder.linkedinPaste')); if (text) handlePasteImport(text); }} className="px-3 py-2 border rounded-md text-sm">{t('button.pasteText')}</button>
                      <button type="button" onClick={() => { setValue('work_experience', []); setValue('education', []); toast(t('button.clearText'), { icon: '🗑️' }); }} className="px-3 py-2 border rounded-md text-sm">{t('button.clearText')}</button>
                    </div>
                  </div>
                  <div className={`flex justify-between items-center mb-4 ${errors.work_experience ? 'p-3 border-2 border-red-300 bg-red-50 rounded-lg' : ''}`}>
                    <div>
                      <h3 className={`text-lg font-medium ${errors.work_experience ? 'text-red-600' : 'text-gray-900'}`}>{t('section.workExperience')}</h3>
                      <p className={`text-sm font-medium ${errors.work_experience ? 'text-red-600' : 'text-red-600'}`}>{t('info.minOneExperience')}</p>
                    </div>
                    <Button
                      type="button"
                      onClick={addWorkExperience}
                      size="sm"
                      className={errors.work_experience ? 'border-red-500 text-red-600 hover:bg-red-100' : ''}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('button.addExperience')}
                    </Button>
                  </div>

                  {/* Show error if no work experience */}
                  {errors.work_experience && (
                    <div className="mb-4 p-3 bg-red-100 border-2 border-red-300 rounded-md">
                      <p className="text-sm text-red-700 font-semibold">
                        ⚠️ {errors.work_experience.message}
                      </p>
                    </div>
                  )}

                  {(workExperience).length === 0 && (
                    <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-sm text-yellow-800">
                        {t('empty.noExperience')}
                      </p>
                    </div>
                  )}

                  <div className="space-y-6">
                    {workExperience.map((_, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-4">
                          <h4 className="text-md font-medium text-gray-700">{t('label.workExperienceItem')} {index + 1}</h4>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeWorkExperience(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('label.company')}
                            </label>
                            <input
                              id={`work_experience_company_name_${index}`}
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              {...register(`work_experience.${index}.company`)}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('label.position')}
                            </label>
                            <input
                              id={`work_experience_role_${index}`}
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              {...register(`work_experience.${index}.position`)}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('label.startDate')}
                            </label>
                            <input
                              type="date"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              {...register(`work_experience.${index}.start_date`)}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('label.endDate')}
                            </label>
                            <input
                              type="date"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              {...register(`work_experience.${index}.end_date`)}
                            />
                            <label className="flex items-center mt-2">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                                {...register(`work_experience.${index}.current`)}
                              />
                              <span className="text-sm">{t('label.currentJob')}</span>
                            </label>
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('label.description')}
                            </label>
                            <textarea
                              id={`work_experience_description_${index}`}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              {...register(`work_experience.${index}.description`)}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-700">
                      {t('info.workExpTip')}
                    </p>
                  </div>
                </div>

                {/* Education */}
                <div className="mb-8" id="education-section">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{t('section.education')}</h3>
                      <p className="text-sm text-red-600 font-medium">{t('info.minOneEducation')}</p>
                    </div>
                    <Button type="button" onClick={addEducation} size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('button.addEducation')}
                    </Button>
                  </div>

                  {/* Show error if no education */}
                  {errors.education && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-600 font-medium">
                        ⚠️ {errors.education.message}
                      </p>
                    </div>
                  )}

                  {(education).length === 0 && (
                    <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-sm text-yellow-800">
                        {t('empty.noEducation')}
                      </p>
                    </div>
                  )}

                  <div className="space-y-6">
                    {education.map((_, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-4">
                          <h4 className="text-md font-medium text-gray-700">{t('label.educationItem')} {index + 1}</h4>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeEducation(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('label.institution')}
                            </label>
                            <input
                              id={`education_institution_${index}`}
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              {...register(`education.${index}.institution`)}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('label.degree')}
                            </label>
                            <input
                              id={`education_degree_${index}`}
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              {...register(`education.${index}.degree`)}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('label.fieldOfStudy')}
                            </label>
                            <input
                              id={`education_field_${index}`}
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              {...register(`education.${index}.field`)}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('label.startYear')}
                            </label>
                            <input
                              type="number"
                              min="1950"
                              max={new Date().getFullYear()}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              {...register(`education.${index}.start_year`)}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('label.endYear')}
                            </label>
                            <input
                              type="number"
                              min="1950"
                              max={new Date().getFullYear() + 10}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              {...register(`education.${index}.end_year`)}
                            />
                            <label className="flex items-center mt-2">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                                {...register(`education.${index}.current`)}
                              />
                              <span className="text-sm">{t('label.currentEducation')}</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-700">
                      {t('info.educationTip')}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {activeTab === 'facturen' && <ProfielFacturenTab />}

          {activeTab !== 'facturen' && (<>
          {/* Submit Button */}
          <div className="mb-6">
            <Card>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('section.completionStatus')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {tabs.map((tab) => {
                    const isComplete = isTabComplete(tab.id);
                    const Icon = tab.icon;
                    return (
                      <div key={tab.id} className="flex items-center justify-between p-2 rounded-md bg-gray-50">
                        <div className="flex items-center">
                          <Icon className="w-4 h-4 mr-2 text-gray-600" />
                          <span className="text-sm font-medium">{tab.name}</span>
                        </div>
                        <div className="flex items-center">
                          {isComplete ? (
                            <span className="text-green-600 text-sm font-medium flex items-center">
                              ✅ {t('tabStatus.complete')}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setActiveTab(tab.id)}
                              className="text-red-600 text-sm font-medium hover:text-red-700 flex items-center"
                            >
                              ❌ {t('tabStatus.incomplete')}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {!tabs.every(tab => isTabComplete(tab.id)) && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-800">
                      ⚠️ <strong>{t('info.completionWarning')}</strong>
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Profiel Compleet-check, preview en publicatie (FO stap 9 & 10).
              De checklist komt van de backend en telt ook zaken mee die buiten
              dit formulier vallen (bedrijfslogo, praktijknaam, tijdslots). */}
          {portfolioStatus && (
            <div className="mb-6">
              <Card>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">{t('section.publication')}</h3>
                    <span className="text-sm font-medium text-gray-700">{portfolioStatus.percentage}%</span>
                  </div>

                  <div className="h-2 w-full rounded-full bg-gray-200" role="progressbar" aria-valuenow={portfolioStatus.percentage} aria-valuemin={0} aria-valuemax={100}>
                    <div
                      className={`h-2 rounded-full transition-all ${portfolioStatus.complete ? 'bg-green-500' : 'bg-blue-500'}`}
                      style={{ width: `${portfolioStatus.percentage}%` }}
                    />
                  </div>

                  <ul className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
                    {portfolioStatus.items.map((item) => (
                      <li key={item.key} className="flex items-center gap-2 rounded-md bg-gray-50 p-2 text-sm">
                        <span>{item.complete ? '✅' : '⬜'}</span>
                        <span className={item.complete ? 'text-gray-700' : 'text-gray-900 font-medium'}>
                          {item.label}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={!portfolioStatus.complete}
                      onClick={() => window.open('/professional/portfolio-preview', '_blank', 'noopener')}
                    >
                      {t('button.previewProfile')}
                    </Button>

                    <Button
                      type="button"
                      disabled={!portfolioStatus.complete || publishPortfolio.isPending}
                      className="bg-green-600 hover:bg-green-700"
                      onClick={async () => {
                        try {
                          await publishPortfolio.mutateAsync();
                          toast.success(t('toast.portfolioPublished'));
                        } catch (error: any) {
                          toast.error(error?.response?.data?.message || t('toast.portfolioPublishError'));
                        }
                      }}
                    >
                      {publishPortfolio.isPending ? t('button.publishing') : t('button.publishPortfolio')}
                    </Button>

                    {portfolioStatus.published && (
                      <span className="text-sm font-medium text-green-700">
                        {t('info.portfolioLive')}
                      </span>
                    )}
                  </div>

                  {!portfolioStatus.complete && (
                    <p className="mt-3 text-sm text-gray-600">{t('info.publicationBlocked')}</p>
                  )}
                </div>
              </Card>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={isLoading || createProfile.isPending}
              className="flex items-center px-8 py-3"
              onClick={(e) => {
                // Ensure form validation runs
                console.log('Button clicked, triggering form validation');
                const form = e.currentTarget.closest('form');
                if (form) {
                  const formData = new FormData(form);
                  console.log('Form data:', Object.fromEntries(formData));
                }
              }}
            >
              {isLoading || createProfile.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('button.saving')}
                </>
              ) : (
                <>
                  <User className="w-4 h-4 mr-2" />
                  {t('button.complete')}
                </>
              )}
            </Button>
          </div>
          </>)}
        </form>
        </div>
      )}
    </div>
  );
};
